import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Loader2, ArrowRight } from 'lucide-react'
import { fetchCollection, formatPrice, getProductImage, type ShopifyProduct } from '../utils/shopify'
import { useSeo, toPlainText } from '../hooks/useSeo'

export default function CollectionPage() {
  const { handle } = useParams<{ handle: string }>()
  const [data, setData] = useState<{ collection: { id: number; title: string; handle: string; body_html?: string; image?: { src: string } | null }; products: ShopifyProduct[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useSeo({
    title: data ? `${data.collection.title} Mouse Mats` : 'Collection',
    description: data ? (toPlainText(data.collection.body_html) || `${data.collection.title} themed custom mouse mats by MatryxLab.`) : undefined,
    image: data?.collection.image?.src || null,
    path: `/collections/${handle}`,
  })

  useEffect(() => {
    if (!handle) return
    setLoading(true)
    fetchCollection(handle)
      .then(setData)
      .catch(() => setError('Collection not found'))
      .finally(() => setLoading(false))
  }, [handle])

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-brand-purple" size={32} />
    </div>
  )

  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-brand-muted">{error || 'Collection not found'}</p>
      <Link to="/catalogue" className="text-brand-purple hover:underline">Back to Catalogue</Link>
    </div>
  )

  const { collection, products } = data

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <Link to="/catalogue" className="inline-flex items-center gap-2 text-brand-muted hover:text-white mb-6 text-sm transition-colors">
          <ArrowLeft size={16} /> Back to Catalogue
        </Link>

        {/* Collection header */}
        <div className="mb-10">
          {collection.image?.src && (
            <div className="w-full h-48 md:h-64 overflow-hidden rounded-2xl mb-6">
              <img src={collection.image.src} alt={collection.title} className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="text-4xl font-bold mb-2">{collection.title}</h1>
          {collection.body_html && (
            <div className="text-brand-muted text-sm max-w-2xl" dangerouslySetInnerHTML={{ __html: collection.body_html }} />
          )}
          <p className="text-sm text-brand-muted mt-2">{products.length} product{products.length !== 1 ? 's' : ''}</p>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-20 text-brand-muted">No products in this collection yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map(product => (
              <Link key={product.id} to={`/products/${product.handle}`} className="card-hover group block">
                <div className="aspect-square relative overflow-hidden bg-brand-surface rounded-xl">
                  <img
                    src={getProductImage(product)}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-brand-purple rounded-full p-1.5">
                      <ArrowRight size={14} className="text-white" />
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <h3 className="font-medium text-sm truncate">{product.title}</h3>
                  <p className="text-brand-purple text-sm font-semibold mt-0.5">
                    {product.variants?.[0]?.price ? formatPrice(product.variants[0].price) : '—'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
